import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { AlertTriangle, TrendingUp } from 'lucide-react';
import type { AttentionItem } from '@/hooks/useCompanyAnalytics';

interface Props {
  items: AttentionItem[];
}

export function AttentionList({ items }: Props) {
  const { t } = useTranslation();

  if (items.length === 0) return null;

  const reasonLabel = (reason: string) => {
    switch (reason) {
      case 'low_punctuality': return t('analytics.reasonLowPunctuality');
      case 'high_incidents': return t('analytics.reasonHighIncidents');
      case 'inactive': return t('analytics.reasonInactive');
      case 'vital_trend': return t('analytics.reasonVitalTrend');
      default: return reason;
    }
  };

  const reasonVariant = (reason: string): 'destructive' | 'outline' => {
    return ['low_punctuality', 'high_incidents', 'vital_trend'].includes(reason) ? 'destructive' : 'outline';
  };

  return (
    <Card className="border-warning/30 dark:border-warning/20">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-warning">
          <AlertTriangle className="h-4 w-4" />
          {t('analytics.attentionNeeded')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.map((item, idx) => (
          <div key={`${item.user_id}-${idx}`} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
            {item.reasons.includes('vital_trend') ? (
              <div className="h-8 w-8 rounded-full bg-destructive/10 flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-destructive" />
              </div>
            ) : (
              <SignedAvatar
                src={item.avatar_url}
                bucket="avatars"
                className="h-8 w-8"
                fallback={
                  <span className="bg-warning/10 text-warning text-xs flex items-center justify-center w-full h-full">
                    {item.name.charAt(0).toUpperCase()}
                  </span>
                }
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {item.patient_nickname ? `${item.patient_nickname}` : item.name}
              </p>
              {item.vital_detail && (
                <p className="text-xs text-muted-foreground truncate">{item.vital_detail}</p>
              )}
              <div className="flex flex-wrap gap-1 mt-1">
                {item.reasons.map((r) => (
                  <Badge key={r} variant={reasonVariant(r)} className="text-[10px] px-1.5 py-0">
                    {reasonLabel(r)}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="text-right text-xs text-muted-foreground">
              {item.on_time_pct !== null && <p>{item.on_time_pct}%</p>}
              <p>{item.total_tasks} {t('analytics.tasksShort')}</p>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
